import { lowerDecodeLimit, type ProjectDecodeLimits } from './ProjectDecodeLimits';
import type { TileInstanceDto } from '@/schemas/tiles';
import { CircuitType, allSides } from '@/schemas/common';
import type { ProjectSnapshotData } from './ProjectSnapshot';

// Disk-only references. Neither authored IDs nor live node handles are renamed.
// [tile ID, type enum, owner, x, y, metadata definition, state definition, bindings?]
type Row = [number, number, number, number, number, number, number, number[]?];
export interface PackedTiles {
  strings: string[];
  owners: [number, 0 | 1][];
  metas: TileInstanceDto['meta'][];
  states: unknown[];
  circuits: Row[][];
}
const MAX_TILES = 1_000_000;
const MAX_TEXT = 64 * 1024 * 1024;
const MAX_EXPANDED_BYTES = 64 * 1024 * 1024;
const MAX_POOL_KEY_BYTES = 64 * 1024;
// Measure before making deduplication keys. Large unique artwork/state must not
// allocate a second project-sized string merely to discover a pool miss.
export function jsonBytes(value: unknown, depth = 0): number {
  if (depth > 128) throw new RangeError('Packed tile metadata is too deep');
  if (value === null || value === undefined) return 4;
  if (typeof value === 'boolean') return value ? 4 : 5;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value).length : 4;
  if (typeof value === 'string') {
    if (value.length > MAX_EXPANDED_BYTES)
      throw new RangeError('Packed tile expansion exceeds manifest limit');
    let bytes = 2;
    for (let i = 0; i < value.length; i++) {
      const code = value.charCodeAt(i);
      if (code === 34 || code === 92) bytes += 2;
      else if (code < 32) bytes += [8, 9, 10, 12, 13].includes(code) ? 2 : 6;
      else if (code < 128) bytes++;
      else if (code < 2048) bytes += 2;
      else if (
        code >= 0xd800 &&
        code <= 0xdbff &&
        value.charCodeAt(i + 1) >= 0xdc00 &&
        value.charCodeAt(i + 1) <= 0xdfff
      ) {
        bytes += 4;
        i++;
      } else bytes += code >= 0xd800 && code <= 0xdfff ? 6 : 3;
      if (bytes > MAX_EXPANDED_BYTES)
        throw new RangeError('Packed tile expansion exceeds manifest limit');
    }
    return bytes;
  }
  const array = Array.isArray(value);
  let bytes = 2,
    count = 0;
  for (const key of Object.keys(value as object)) {
    const item = (value as Record<string, unknown>)[key];
    if (!array && item === undefined) continue;
    bytes +=
      (count++ ? 1 : 0) +
      (array ? 0 : jsonBytes(key, depth + 1) + 1) +
      jsonBytes(item, depth + 1);
    if (bytes > MAX_EXPANDED_BYTES)
      throw new RangeError('Packed tile expansion exceeds manifest limit');
  }
  return bytes;
}

// File metadata is a small JSON tree. Copying it directly avoids thousands of
// structuredClone calls while retaining independent nested mutable values.
function copyMetadata(value: unknown, depth = 0): any {
  if (!value || typeof value !== 'object') return value;
  if (depth > 128) throw new RangeError('Packed tile metadata is too deep');
  if (Array.isArray(value)) return value.map((child) => copyMetadata(child, depth + 1));
  const copy = Object.create(null);
  for (const key of Object.keys(value))
    copy[key] = copyMetadata((value as Record<string, unknown>)[key], depth + 1);
  return copy;
}

export function packTiles(project: ProjectSnapshotData): {
  project: ProjectSnapshotData;
  tiles: PackedTiles;
} {
  const tiles: PackedTiles = {
    strings: [],
    owners: [],
    metas: [],
    states: [],
    circuits: [],
  };
  const strings = new Map<string, number>();
  const metas = new Map<string, number>();
  const states = new Map<string, number>();
  const owners = new Map<string, number>();
  let count = 0,
    text = 0,
    expanded = 0;
  const intern = (value: string): number => {
    let index = strings.get(value);
    if (index !== undefined) return index;
    if ((text += value.length) > MAX_TEXT || strings.size >= MAX_TILES * 4)
      throw new RangeError('Packed tile text exceeds limit');
    index = tiles.strings.length;
    strings.set(value, index);
    tiles.strings.push(value);
    return index;
  };
  const circuit = (value: ProjectSnapshotData['chips'][number]['logicCircuitDto']) => {
    const rows: Row[] = [];
    tiles.circuits.push(rows);
    for (const tile of value.tileManagerDto.tiles) {
      if (++count > MAX_TILES) throw new RangeError('Packed tile count exceeds limit');
      const { chipInstanceId, circuitType, location } = tile.globalTileLocation;
      const metaCost = jsonBytes(tile.meta),
        stateCost = jsonBytes(tile.state);
      expanded +=
        320 +
        jsonBytes(tile.id) +
        jsonBytes(tile.tileType) +
        jsonBytes(chipInstanceId) +
        metaCost +
        stateCost +
        Object.values(tile.logicNodeIds ?? {}).reduce(
          (sum, id) => sum + jsonBytes(id) + 10,
          0,
        );
      if (expanded > MAX_EXPANDED_BYTES)
        throw new RangeError('Packed tile expansion exceeds manifest limit');
      const owner = [
        intern(chipInstanceId),
        circuitType === CircuitType.ChipLogic ? 0 : 1,
      ] as [number, 0 | 1];
      const ownerKey = owner.join(',');
      let ownerId = owners.get(ownerKey);
      if (ownerId === undefined) {
        ownerId = tiles.owners.length;
        owners.set(ownerKey, ownerId);
        tiles.owners.push(owner);
      }
      // Metadata is already schema-validated, owned plain data. Undefined/-0
      // distinctions are restored at original paths by MetadataScalars.
      const metaKey =
        metaCost <= MAX_POOL_KEY_BYTES ? JSON.stringify(tile.meta) : undefined;
      let metaId = metaKey === undefined ? undefined : metas.get(metaKey);
      if (metaId === undefined) {
        metaId = tiles.metas.length;
        if (metaKey !== undefined) metas.set(metaKey, metaId);
        tiles.metas.push(tile.meta);
      }
      const stateKey =
        stateCost <= MAX_POOL_KEY_BYTES ? JSON.stringify(tile.state) : undefined;
      let stateId = stateKey === undefined ? undefined : states.get(stateKey);
      if (stateId === undefined) {
        stateId = tiles.states.length;
        if (stateKey !== undefined) states.set(stateKey, stateId);
        tiles.states.push(tile.state);
      }
      const row: Row = [
        intern(tile.id),
        intern(tile.tileType),
        ownerId,
        location.x,
        location.y,
        metaId,
        stateId,
      ];
      if (tile.logicNodeIds !== undefined) {
        let mask = 0;
        const refs: number[] = [];
        allSides.forEach((side, i) => {
          const id = tile.logicNodeIds![side];
          if (id !== undefined) {
            mask |= 1 << i;
            refs.push(intern(id));
          }
        });
        row.push([mask, ...refs]);
      }
      rows.push(row);
    }
    return { ...value, tileManagerDto: { tiles: [] } };
  };
  const result = {
    project: {
      ...project,
      chips: project.chips.map((chip) => ({
        ...chip,
        logicCircuitDto: circuit(chip.logicCircuitDto),
        structureCircuitDto: circuit(chip.structureCircuitDto),
      })),
    },
    tiles,
  };
  // The incremental encoder charges the same expanded layout before append;
  // do not traverse/serialize every definition a second time on normal saves.
  return result;
}

/** Validate all references and expanded costs before allocating tile objects.
 * Each restored tile gets fresh metadata; mutable DTO consumers never alias. */
export function validatePackedTiles(
  project: ProjectSnapshotData,
  input: unknown,
  limits?: ProjectDecodeLimits,
): asserts input is PackedTiles {
  const maximumTiles = lowerDecodeLimit(limits?.tiles, MAX_TILES);
  const maximumBytes = lowerDecodeLimit(limits?.tileBytes, MAX_EXPANDED_BYTES);
  if (!input || typeof input !== 'object' || Array.isArray(input))
    throw new TypeError('Invalid packed tiles');
  const packed = input as PackedTiles;
  if (
    Object.keys(packed).length !== 5 ||
    !Array.isArray(packed.strings) ||
    packed.strings.length > MAX_TILES * 4 ||
    !Array.isArray(packed.owners) ||
    packed.owners.length > MAX_TILES ||
    !Array.isArray(packed.metas) ||
    packed.metas.length > MAX_TILES ||
    !Array.isArray(packed.states) ||
    packed.states.length > MAX_TILES ||
    !Array.isArray(packed.circuits) ||
    !Array.isArray(project?.chips) ||
    packed.circuits.length !== project.chips.length * 2
  )
    throw new TypeError('Invalid packed tile tables');
  let text = 0;
  for (const value of packed.strings)
    if (typeof value !== 'string' || (text += value.length) > MAX_TEXT)
      throw new RangeError('Packed tile text exceeds limit');
  const ref = (value: unknown, length: number): value is number =>
    Number.isSafeInteger(value) && (value as number) >= 0 && (value as number) < length;
  for (const owner of packed.owners)
    if (
      !Array.isArray(owner) ||
      owner.length !== 2 ||
      !ref(owner[0], packed.strings.length) ||
      (owner[1] !== 0 && owner[1] !== 1)
    )
      throw new TypeError('Invalid packed tile owner');
  const metaBytes = packed.metas.map((meta) => {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta))
      throw new TypeError('Invalid packed tile metadata');
    return jsonBytes(meta);
  });
  const stateBytes = packed.states.map((value) => jsonBytes(value));
  let count = 0,
    expanded = 0;
  const costs = packed.strings.map((value) => jsonBytes(value));
  for (const rows of packed.circuits) {
    if (!Array.isArray(rows) || (count += rows.length) > maximumTiles)
      throw new RangeError('Packed tile count exceeds limit');
    for (const row of rows) {
      if (
        !Array.isArray(row) ||
        (row.length !== 7 && row.length !== 8) ||
        !ref(row[0], packed.strings.length) ||
        !ref(row[1], packed.strings.length) ||
        !ref(row[2], packed.owners.length) ||
        !ref(row[5], packed.metas.length) ||
        !ref(row[6], packed.states.length) ||
        typeof row[3] !== 'number' ||
        !Number.isFinite(row[3]) ||
        typeof row[4] !== 'number' ||
        !Number.isFinite(row[4])
      )
        throw new TypeError('Invalid packed tile record');
      let bindingCost = 0;
      if (row.length === 8) {
        const bindings = row[7];
        if (
          !Array.isArray(bindings) ||
          !Number.isInteger(bindings[0]) ||
          bindings[0] < 0 ||
          bindings[0] > 15 ||
          bindings.length !==
            1 + allSides.filter((_, i) => bindings[0] & (1 << i)).length ||
          !bindings.slice(1).every((id) => ref(id, packed.strings.length))
        )
          throw new TypeError('Invalid packed tile bindings');
        bindingCost = bindings.slice(1).reduce((sum, id) => sum + costs[id] + 10, 0);
      }
      // Conservative upper bound includes field names, owner/type enums and
      // punctuation. Pooled state is charged for every decoded occurrence.
      expanded +=
        320 +
        costs[row[0]] +
        costs[row[1]] +
        costs[packed.owners[row[2]][0]] +
        metaBytes[row[5]] +
        stateBytes[row[6]] +
        bindingCost;
      if (expanded > maximumBytes)
        throw new RangeError('Packed tile expansion exceeds manifest limit');
    }
  }
  for (const chip of project.chips)
    for (const circuit of [chip.logicCircuitDto, chip.structureCircuitDto])
      if (
        !circuit?.tileManagerDto ||
        !Array.isArray(circuit.tileManagerDto.tiles) ||
        circuit.tileManagerDto.tiles.length
      )
        throw new TypeError('Unexpected unpacked tiles in compact manifest');
}

export function unpackTiles(
  project: ProjectSnapshotData,
  input: unknown,
  limits?: ProjectDecodeLimits,
): ProjectSnapshotData {
  validatePackedTiles(project, input, limits);
  const packed = input;
  let index = 0;
  const circuit = (value: ProjectSnapshotData['chips'][number]['logicCircuitDto']) => {
    if (
      !value?.tileManagerDto ||
      !Array.isArray(value.tileManagerDto.tiles) ||
      value.tileManagerDto.tiles.length
    )
      throw new TypeError('Unexpected unpacked tiles in compact manifest');
    const tiles = packed.circuits[index++].map((row) => {
      const owner = packed.owners[row[2]];
      const tile: TileInstanceDto = {
        id: packed.strings[row[0]],
        tileType: packed.strings[row[1]] as TileInstanceDto['tileType'],
        globalTileLocation: {
          chipInstanceId: packed.strings[owner[0]],
          circuitType: owner[1] === 0 ? CircuitType.ChipLogic : CircuitType.ChipStructure,
          location: { x: row[3], y: row[4] },
        },
        meta: copyMetadata(packed.metas[row[5]]),
        // decodeStateValue reconstructs a fresh value for every tile after
        // schema validation; tagged definitions are never mutable DTO state.
        state: packed.states[row[6]],
      };
      if (row.length === 8) {
        tile.logicNodeIds = {};
        let next = 1;
        allSides.forEach((side, i) => {
          if (row[7]![0] & (1 << i))
            tile.logicNodeIds![side] = packed.strings[row[7]![next++]];
        });
      }
      return tile;
    });
    return { ...value, tileManagerDto: { tiles } };
  };
  return {
    ...project,
    chips: project.chips.map((chip) => ({
      ...chip,
      logicCircuitDto: circuit(chip.logicCircuitDto),
      structureCircuitDto: circuit(chip.structureCircuitDto),
    })),
  };
}
